/* eslint-disable max-lines */
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import { useSearch } from "../../hooks/useSearch";
import {
  IPayoutProcessDto,
  IPayoutRequest
} from "../../interfaces/endpoints/IAffiliates";
import {
  getPayoutRequests,
  processPayout
} from "../../services/Affiliates.service";
import Button from "../ui/Button";
import Input from "../ui/input/Input";
import Select from "../ui/input/Select";
import Textarea from "../ui/input/Textarea";
import Modal from "../ui/Modal";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";
import { PayoutStatusBadge } from "./AffiliateStatusBadge";

const columns = [
  "Partneri",
  "Email",
  "Shuma e kerkuar",
  "Shuma e aprovuar",
  "Statusi",
  "Banka",
  "IBAN",
  "Mbajtesi",
  "Data",
  "Veprimet"
];

const payoutStatusOptions = [
  { name: "Aprovuar", id: "1" },
  { name: "Refuzuar", id: "3" }
];

const PayoutsList = () => {
  const { response, loading, handlePageChange, page, refetch } = useSearch(
    getPayoutRequests,
    {
      withQuerySearch: false,
      withFilter: true,
      filterSelector: "status"
    }
  );

  const [showModal, setShowModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<IPayoutRequest | null>(
    null
  );
  const [processForm, setProcessForm] = useState<IPayoutProcessDto>({
    status: 1,
    approvedAmount: 0,
    adminNote: ""
  });

  const openProcessModal = (payout: IPayoutRequest) => {
    setSelectedPayout(payout);
    setProcessForm({
      status: 1,
      approvedAmount: payout.requestedAmount,
      adminNote: ""
    });
    setShowModal(true);
  };

  const handleProcess = async () => {
    if (!selectedPayout) return;
    if (processForm.approvedAmount <= 0 || processForm.approvedAmount > selectedPayout.requestedAmount) {
      toast.error("Shuma e aprovuar duhet te jete midis 0 dhe shumes se kerkuar");
      return;
    }
    try {
      await processPayout(selectedPayout.id, processForm);
      toast.success("Pagesa u procesua me sukses");
      setShowModal(false);
      refetch();
    } catch {
      toast.error("Deshtoi procesimi i pageses");
    }
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {(response?.data.result || []).map((row: IPayoutRequest, i: number) => (
          <Row
            key={i}
            affiliateName={row.affiliateName}
            affiliateEmail={row.affiliateEmail}
            requestedAmount={`€${row.requestedAmount.toFixed(2)}`}
            approvedAmount={
              row.approvedAmount > 0 ? `€${row.approvedAmount.toFixed(2)}` : "-"
            }
            status={<PayoutStatusBadge status={row.status} />}
            bankName={row.bankName || "-"}
            iban={row.iban || "-"}
            accountHolderName={row.accountHolderName || "-"}
            insertDate={dayjs(row.insertDate).format("DD-MM-YYYY")}
            actions={
              <Can permissions="affiliates.payout.process">
                {row.status === 0 && (
                  <Button
                    padding="px-3 py-1.5"
                    onClick={() => openProcessModal(row)}
                  >
                    Proceso
                  </Button>
                )}
              </Can>
            }
          />
        ))}
      </Table>
      <Pagination
        onPageChange={handlePageChange}
        totalCount={response?.data.totalCount || 0}
        length={response?.data?.result?.length}
        page={page}
        pageSize={10}
      />

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Proceso pagesen"
        position="center"
        buttonElement={
          <Button onClick={handleProcess}>Konfirmo</Button>
        }
      >
        <div className="p-4 space-y-4">
          {selectedPayout && (
            <>
              <p className="text-sm text-gray-600">
                Partneri: <strong>{selectedPayout.affiliateName}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Shuma e kerkuar:{" "}
                <strong>€{selectedPayout.requestedAmount.toFixed(2)}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Banka: <strong>{selectedPayout.bankName || "-"}</strong>
              </p>
              <p className="text-sm text-gray-600">
                IBAN: <strong>{selectedPayout.iban || "-"}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Mbajtesi i llogarise: <strong>{selectedPayout.accountHolderName || "-"}</strong>
              </p>
              {selectedPayout.note && (
                <p className="text-sm text-gray-600">
                  Shenim nga partneri: <strong>{selectedPayout.note}</strong>
                </p>
              )}
            </>
          )}
          <Select
            label="Statusi"
            options={payoutStatusOptions}
            value={processForm.status}
            onChange={(e: any) =>
              setProcessForm({
                ...processForm,
                status: Number(e.target.value)
              })
            }
            hasEmptyOption={false}
          />
          <Input
            label="Shuma e aprovuar"
            type="number"
            min={0}
            max={selectedPayout?.requestedAmount}
            step="0.01"
            value={processForm.approvedAmount}
            onChange={(e: any) =>
              setProcessForm({
                ...processForm,
                approvedAmount: Number(e.target.value)
              })
            }
          />
          <Textarea
            label="Shenime nga admini"
            value={processForm.adminNote}
            onChange={(e: any) =>
              setProcessForm({ ...processForm, adminNote: e.target.value })
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default PayoutsList;
